import React, { useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import AddAssetsToShare from 'src/components/AddAssetsToShare';
import CreateCollectionModal from 'src/components/CreateCollectionModal';
import { useAccessToken } from 'src/hooks/useAccessToken';
import useModal from 'src/hooks/useModal';
import useRoleManager from 'src/hooks/useRoleManager';
import { AssetRow } from 'src/types/TDeliveryAnalytics';
import { formatBillingBytes, formatNumberIntl } from 'src/utils/common';
import { getThumbnailUrl } from 'src/utils/previews';
import styles from '../styles.module.scss';

/** Collection/share actions operate on at most this many of the listed assets. */
const ACTION_CAP = 100;

const AssetThumb: React.FC<{ assetId: string; jwt: string }> = ({ assetId, jwt }) => {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return <span className={styles.thumbPlaceholder} aria-hidden />;
  }
  return (
    <img
      className={styles.thumb}
      src={getThumbnailUrl({ id: assetId, width: 80, height: 80, jwt })}
      alt=""
      loading="lazy"
      onError={() => setErrored(true)}
    />
  );
};

const AssetsTable: React.FC<{ rows: AssetRow[]; showActions?: boolean }> = ({
  rows,
  showActions,
}) => {
  const jwt = useAccessToken();
  const location = useLocation();
  const history = useHistory();
  const { showModal } = useModal();
  const roleManager = useRoleManager();

  const ids = rows.map((r) => r.asset_id).filter(Boolean).slice(0, ACTION_CAP);
  const canAct = showActions && !roleManager.isUser && ids.length > 0;

  const assetLink = (assetId: string) => ({
    pathname: `/asset/${assetId}`,
    state: { background: location },
  });

  return (
    <>
      {canAct && (
        <div className={styles.assetActions}>
          {rows.length > ACTION_CAP && (
            <span className={styles.assetActionsNote}>
              Acts on the top {ACTION_CAP} assets
            </span>
          )}
          <button
            type="button"
            className={styles.clearButton}
            onClick={() =>
              showModal(
                <CreateCollectionModal
                  assetIds={ids}
                  onCreateCollection={() => history.push('/collections')}
                />,
              )
            }
          >
            Create collection
          </button>
          <button
            type="button"
            className={styles.clearButton}
            onClick={() => showModal(<AddAssetsToShare assetIds={ids} />)}
          >
            Share
          </button>
        </div>
      )}
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.thumbCol} />
            <th>Asset ID</th>
            <th className={styles.numCol}>Requests</th>
            <th className={styles.numCol}>Bytes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.asset_id || '(unknown)'}>
              <td className={styles.thumbCol}>
                {r.asset_id ? (
                  <Link to={assetLink(r.asset_id)} aria-label={`Open asset ${r.asset_id}`}>
                    <AssetThumb assetId={r.asset_id} jwt={jwt} />
                  </Link>
                ) : (
                  <span className={styles.thumbPlaceholder} aria-hidden />
                )}
              </td>
              <td className={styles.mono}>
                {r.asset_id ? (
                  <Link to={assetLink(r.asset_id)} className={styles.assetLink}>
                    {r.asset_id}
                  </Link>
                ) : (
                  '(unknown)'
                )}
              </td>
              <td className={styles.numCol}>{formatNumberIntl(r.requests)}</td>
              <td className={styles.numCol}>{formatBillingBytes(r.bytes)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default AssetsTable;
